import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { label: "Why Now", href: "#why-now" },
  { label: "Batches", href: "#batches" },
  { label: "Founders", href: "#founders" },
  { label: "Schedule", href: "#schedule" },
  { label: "Fees", href: "#fees" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-[#F7F5F0]/85 backdrop-blur-md border-b hairline border-b-[1px]" : "bg-transparent"
        }`}
        data-testid="site-nav"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <a href="#top" className="flex items-baseline gap-2" data-testid="brand-mark">
            <span className="font-display text-2xl md:text-3xl leading-none tracking-tight">
              shemushi<span style={{ color: "var(--brand)" }}>.</span>
            </span>
            <span className="hidden md:inline font-mono-editorial text-[10px] text-[color:var(--muted)] tracking-[0.28em]">
              शेमुषी
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-sm link-underline text-[color:var(--ink)]/80 hover:text-[color:var(--ink)]"
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
          </nav>

          <a
            href="#enroll"
            className="hidden md:inline-flex items-center gap-2 h-10 px-5 bg-[color:var(--ink)] text-[color:var(--inv)] rounded-full text-sm font-medium hover:bg-[color:var(--brand)] transition-colors duration-500"
            data-testid="nav-enroll-btn"
          >
            Enroll for Inner Compass
            <span aria-hidden>→</span>
          </a>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center border hairline rounded-full"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="nav-menu-open"
          >
            <Menu size={18} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[color:var(--ink)] text-[color:var(--inv)] md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 h-16 border-b border-white/10">
              <span className="font-display text-2xl">shemushi<span style={{ color: "var(--brand)" }}>.</span></span>
              <button
                className="w-10 h-10 flex items-center justify-center border border-white/20 rounded-full"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="nav-menu-close"
              >
                <X size={18} />
              </button>
            </div>
            <nav className="px-6 py-10 flex flex-col gap-6">
              {links.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.5 }}
                  className="font-display text-4xl"
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {l.label}
                </motion.a>
              ))}
              <a
                href="#enroll"
                onClick={() => setOpen(false)}
                className="mt-6 inline-flex items-center justify-between px-5 py-4 bg-[color:var(--brand)] text-white rounded-full"
                data-testid="mobile-enroll-btn"
              >
                Enroll for Inner Compass <span>→</span>
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
